import { Link } from "react-router-dom";
import { server } from "../../server";
import axios from "axios";
import * as XLSX from "xlsx";
import { useEffect, useRef, useState } from "react";
import { getDecodedToken } from "../../auth";
import { toast } from "react-toastify";

function CollectedBySummary() {
  const [collectedBy, setCollectedBy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);
  const recordsPerPage = 10;

  const [user, setUser] = useState(null);

  const userRole = user?.roles;
  const userId = user?.userId;

  useEffect(() => {
    const decodedData = getDecodedToken();
    if (decodedData) {
      setUser(decodedData);
    }
  }, []);

  const fetchCollectedBy = async () => {
    try {
      setLoading(true);

      const today = new Date();
      const formattedToday = today.toISOString().split("T")[0];
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const formattedYesterday = yesterday.toISOString().split("T")[0];

      const response = await axios.get(
        `${server}/FilterWithCollectedByAndDateRange?fromDate=${formattedYesterday}&toDate=${formattedToday}`,
        {
          headers: { Authorization: localStorage.getItem("token") },
        }
      );

      const responseData = response.data;
      if (!Array.isArray(responseData)) {
        toast.error("No Records Found", { position: "top-right" });
        return;
      }

      setCollectedBy(responseData);
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole && Array.isArray(userRole)) {
      fetchCollectedBy();
    }
  }, [userRole]);

  const handleDownloadFromToDateWiseReport = async () => {
    try {
      const fromDate = fromDateInputRef.current?.value;
      const toDate = toDateInputRef.current?.value;

      if (!fromDate || !toDate) {
        toast.error(!fromDate ? "Select From Date" : "Select To Date", {
          position: "top-right",
        });
        return;
      }

      const response = await axios.get(
        `${server}/FilterWithCollectedByAndDateRange?fromDate=${fromDate}&toDate=${toDate}`,
        {
          headers: { Authorization: localStorage.getItem("token") },
        }
      );

      const responseData =
        response.data?.["body-json"]?.body?.feeHistory || response.data;

      if (!Array.isArray(responseData) || responseData.length === 0) {
        toast.error("No Records Found", { position: "top-right" });
        return;
      }

      const workbook = XLSX.utils.book_new();

      responseData.forEach((collector) => {
        const flattenedData = [];

        const sortedDates = [...collector.dates].sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        sortedDates.forEach((dateEntry) => {
          dateEntry.paymentModes.forEach((pm) => {
            flattenedData.push({
              Date: new Date(dateEntry.date).toLocaleDateString("en-GB"),
              PaymentMode: pm.mode,
              Amount: pm.totalAmount,
            });
          });

          flattenedData.push({
            Date: new Date(dateEntry.date).toLocaleDateString("en-GB"),
            PaymentMode: "Total Collection",
            Amount: dateEntry.dayTotal,
          });
          flattenedData.push({});
        });

        const worksheet = XLSX.utils.json_to_sheet(flattenedData, {
          header: ["Date", "PaymentMode", "Amount"],
        });

        const sheetName =
          collector.collectedBy.length > 31
            ? collector.collectedBy.slice(0, 31)
            : collector.collectedBy;

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      const fileName = `Report_${fromDate}_to_${toDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success("Report Generated");
    } catch (error) {
      toast.error("Please Try Again", { position: "top-right" });
    }
  };

  // Filter records based on search term
  const filteredRecords = collectedBy.filter((feesMaster) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(feesMaster || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page navigation functions
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div id="db-wrapper">
      <main id="page-content">
        <div className="">
          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {currentRecords.map((record, index) => {
                        return (
                          <tr key={index}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Collected By</span>{" "}
                              &mdash;{" "}
                              <span className="text-uppercase">
                                {record.collectedBy}
                              </span>
                              <br />
                              <span className="text-dark">
                                Grand Total
                              </span>{" "}
                              &mdash; {record.grandTotal.toLocaleString()}
                              {record.dates.map((day, dIdx) => (
                                <div key={dIdx}>
                                  <span className="text-dark">Date</span>{" "}
                                  &mdash;{" "}
                                  {new Date(day.date).toLocaleDateString(
                                    "en-GB"
                                  )}
                                  <br />
                                  <br />
                                  {day.paymentModes.map((pm, pmIdx) => (
                                    <span key={pmIdx} className="d-block">
                                      <span className="text-dark">
                                        {pm.mode}
                                      </span>{" "}
                                      &mdash; {pm.totalAmount.toLocaleString()}
                                    </span>
                                  ))}
                                </div>
                              ))}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 width-fit-content">
                                <span className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                  PDF Report
                                </span>
                                <Link className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                  Excel Report
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="bg-white rounded-pill">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Fees Collection</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            CollectedBy Summary
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          CollectedBy Summary
                          <br />
                          <span className="text-secondary fw-medium fs-6 lh-sm">
                            0.2 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Today's Report
                              </span>
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Today's Collection Amount
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>

                    {(userRole?.includes("Master Admin") ||
                      userRole?.includes("Admin")) && (
                      <>
                        <div className="bg-secondary-soft rounded-4 py-3 px-4 mt-1">
                          <div>
                            <input
                              type="date"
                              className="form-control ps-4 pe-5 border-0 rounded-pill"
                              placeholder="..."
                              ref={fromDateInputRef}
                            />
                            <input
                              type="date"
                              className="form-control ps-4 pe-5 border-0 rounded-pill mt-1"
                              placeholder="..."
                              ref={toDateInputRef}
                            />
                          </div>
                          <span
                            onClick={handleDownloadFromToDateWiseReport}
                            className="btn btn-primary btn-sm px-4 rounded-pill mt-2 me-1"
                          >
                            Generate Report
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CollectedBySummary;
